import React, { FC } from "react";
import { IconButton, ButtonGroup, Select, Option } from "@material-tailwind/react";
import { ArrowRightIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";

export type PaginationProps = {
  total: number;
  page: number;
  limit: number;
  onClick: (page: number) => void;
  onLimitChange: (limit: number) => void;
  limitOptions?: number[];
};

const Pagination: FC<PaginationProps> = ({
  total = 0,
  page,
  limit,
  onClick,
  onLimitChange,
  limitOptions = [10, 20, 50, 100],
}) => {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const startItem = Math.min(total, (page - 1) * limit + 1);
  const endItem = Math.min(total, (page - 1) * limit + limit);

  const createPageItems = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (page > 4) {
        pages.push("...");
      }
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (page < totalPages - 3) {
        pages.push("...");
      }
      pages.push(totalPages);
    }
    return pages;
  };

  const pageItems = createPageItems();
  const isFirst = page === 1;
  const isLast = page === totalPages;

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-blue-gray-100 bg-white p-3 shadow-sm sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:p-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-2 whitespace-nowrap">
          <span className="text-xs text-blue-gray-600 sm:text-sm">Items per page</span>
          <Select
            value={String(limit)}
            containerProps={{ className: "!relative !h-10 !w-16 !min-w-0" }}
            onChange={(value) => {
              if (value) {
                onLimitChange(parseInt(value));
              }
            }}
          >
            {limitOptions.map((opt) => (
              <Option key={opt} value={String(opt)}>
                {opt}
              </Option>
            ))}
          </Select>
        </div>
        <div className="whitespace-nowrap text-xs text-blue-gray-600 sm:text-sm">
          {startItem} - {endItem} of {total} item
        </div>
      </div>

      <ButtonGroup
        variant="outlined"
        className="flex w-max max-w-full shrink-0 flex-nowrap items-center !gap-0 !shadow-none"
      >
        <IconButton
          onClick={() => !isFirst && onClick(page - 1)}
          disabled={isFirst}
          aria-label="Previous page"
          className="!h-8 !w-8 !min-w-0 !shrink-0 !rounded-md !p-0 text-xs"
        >
          <ArrowLeftIcon strokeWidth={2} className="h-3.5 w-3.5" />
        </IconButton>

        {pageItems.map((item, index) =>
          typeof item === "number" ? (
            <IconButton
              key={item}
              onClick={() => onClick(item as number)}
              className={`!h-8 !w-8 !min-w-0 !shrink-0 !rounded-md !p-0 text-xs ${
                page === item
                  ? "!bg-blue-gray-900 !font-bold !text-white"
                  : ""
              }`}
              aria-current={page === item ? "page" : undefined}
              aria-label={`Page ${item}`}
            >
              {item}
            </IconButton>
          ) : (
            <IconButton
              key={`ellipsis-${index}`}
              disabled
              className="!h-8 !w-8 !min-w-0 !shrink-0 !rounded-md !p-0 text-xs"
            >
              <span>{item}</span>
            </IconButton>
          )
        )}

        <IconButton
          onClick={() => !isLast && onClick(page + 1)}
          disabled={isLast}
          aria-label="Next page"
          className="!h-8 !w-8 !min-w-0 !shrink-0 !rounded-md !p-0 text-xs"
        >
          <ArrowRightIcon strokeWidth={2} className="h-3.5 w-3.5" />
        </IconButton>
      </ButtonGroup>
    </div>
  );
};

export default Pagination;
